import React, {useState} from 'react';
import {View} from 'react-native';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {ErrorMessage, Formik} from 'formik';
import {observer} from 'mobx-react-lite';
import * as Yup from 'yup';

import Button from '../../components/themed/Button';
import Text from '../../components/themed/Text';
import TextInput from '../../components/themed/TextInput';
import ValidationMessage from '../../components/themed/ValidationMessage';
import AddressEntry from '../../components/ui-controls/address/AddressEntry';
import ContentView from '../../components/ui-controls/layout/ContentView';
import WeakPassword from '../../components/WeakPassword';
import {AppRoute} from '../../models/AppRoute';
import {Address} from '../../models/mfl';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';
import TogglePasswordVisibleIconButton from '../account/components/TogglePasswordVisibleIconButton';

import {
  FullSignUpSchema,
  passwordOptional,
  passwordValidationBypassRequired,
} from './validationSchema';

const RewardsFullSignUpScreen = observer(
  ({route}: RouteNavigatorScreenProps<AppRoute.REWARDS_FULL_SIGN_UP>) => {
    const {firstName} = route.params;

    const {userStore} = useStores();

    const validationSchema = FullSignUpSchema.concat(
      userStore.showWeakPasswordCheckbox
        ? passwordValidationBypassRequired
        : passwordOptional,
    );

    type FullSignUpValues = Yup.InferType<typeof validationSchema>;

    const defaultValues: FullSignUpValues = {
      password: '',
      confirm: '',
      billingAddress: {
        lines: [],
        locality: '',
        administrative_area: '',
        postal_code: '',
      },
      passwordValidationBypass: false,
    };

    return (
      <KeyboardAwareScrollView keyboardShouldPersistTaps="always">
        <Formik
          initialValues={defaultValues}
          validationSchema={validationSchema}
          onSubmit={async (data, formikHelpers) => {
            await userStore.rewardsFullSignup(data);
          }}>
          {({
            errors,
            handleBlur,
            handleChange,
            initialValues,
            setFieldValue,
            touched,
            values,
            isSubmitting,
            handleSubmit,
          }) => {
            const [isPasswordVisible, setPasswordVisible] = useState(false);
            const [isConfirmVisible, setConfirmVisible] = useState(false);

            const handleAddressChange = (value: Address) => {
              setFieldValue('billingAddress', value);
            };

            const handlePasswordValidationBypass = (value: boolean) => {
              setFieldValue('passwordValidationBypass', value);
            };

            const handleTogglePasswordVisible = () =>
              setPasswordVisible(!isPasswordVisible);

            const handleToggleConfirmVisible = () =>
              setConfirmVisible(!isConfirmVisible);

            return (
              <ContentView>
                <Text variant="heading1">
                  Hey {firstName || 'there'}! Let’s finish creating your
                  account!
                </Text>

                <Text style={{marginBottom: 20}}>
                  Enter your details below for full access to online shopping!
                </Text>

                <View style={{gap: 8, zIndex: 1000}}>
                  <TextInput
                    autoCapitalize="none"
                    defaultValue={initialValues.password}
                    label="Password"
                    onBlur={handleBlur('password')}
                    onChangeText={handleChange('password')}
                    errorMessage={
                      touched.password && errors.password
                        ? errors.password
                        : 'Please enter a strong password with a mix of letters, numbers and symbols'
                    }
                    errorStyle={
                      touched.password && errors.password
                        ? {}
                        : {color: '#6F6F6F', fontStyle: 'italic'}
                    }
                    secureTextEntry={!isPasswordVisible}
                    touched={touched.password}
                    errorMessageWhenTouched={errors.password}
                    rightIcon={
                      <View style={{position: 'absolute', right: 10}}>
                        <TogglePasswordVisibleIconButton
                          isPasswordVisible={isPasswordVisible}
                          onPress={handleTogglePasswordVisible}
                        />
                      </View>
                    }
                  />

                  {!!userStore.showWeakPasswordCheckbox && (
                    <WeakPassword
                      checked={values.passwordValidationBypass}
                      onChange={handlePasswordValidationBypass}
                      errorMessage={errors.passwordValidationBypass}
                    />
                  )}

                  <TextInput
                    autoCapitalize="none"
                    defaultValue={initialValues.password}
                    label="Confirm password"
                    onBlur={handleBlur('confirm')}
                    onChangeText={handleChange('confirm')}
                    secureTextEntry={!isConfirmVisible}
                    touched={touched.confirm}
                    errorMessageWhenTouched={errors.confirm}
                    rightIcon={
                      <View style={{position: 'absolute', right: 10}}>
                        <TogglePasswordVisibleIconButton
                          isPasswordVisible={isConfirmVisible}
                          onPress={handleToggleConfirmVisible}
                        />
                      </View>
                    }
                  />
                  <Text variant="body2">Billing address</Text>
                  <AddressEntry onChange={handleAddressChange} placeholder="" />
                  {!values.billingAddress &&
                  !!errors.billingAddress &&
                  !!touched.billingAddress ? (
                    <ValidationMessage>
                      This is a required field
                    </ValidationMessage>
                  ) : (
                    <ValidationMessage>
                      <ErrorMessage name="billingAddress.lines" />
                      <ErrorMessage name="billingAddress.locality" />
                      <ErrorMessage name="billingAddress.administrative_area" />
                      <ErrorMessage name="billingAddress.postal_code" />
                    </ValidationMessage>
                  )}
                </View>

                <View style={{gap: 8, marginTop: 40}}>
                  <Button
                    title={'FINISH SIGNING UP'}
                    onPress={handleSubmit}
                    disabled={isSubmitting}
                    loading={isSubmitting}
                  />
                </View>
              </ContentView>
            );
          }}
        </Formik>
      </KeyboardAwareScrollView>
    );
  },
);

export default RewardsFullSignUpScreen;
